//
// Copyright (c) 2026 Comcast
//

import PropTypes from 'prop-types'
import { useState } from 'react'
import styled, { css } from 'styled-components'
import colors from 'components/utils/colors'
import IconLink from 'components/Shared/IconLink/Index'
import DynamicTextArea from 'components/Shared/DynamicTextArea/Index'

const PADDING_HORIZ = 7
const HEIGHT = 32
const PADDING_WITH_ICON = 25
const BORDER_SIZE = 1
const STRIPES_COLOR = '#00000012'
const STRIPES_WIDTH_PERCENT = 5
const STRIPES_BOX_SIZE_PX = 5

const StyledInputFieldWrapper = styled.div`
  width: ${props => props.width || '100%'};
  position: relative;
  display: flex;
  align-items: center;
`

const styleInputAsError = css`
  && {
    border-color: ${colors.alert};
  }
`

const styleInputWithIcon = css`
  padding-right: ${PADDING_WITH_ICON}px;
`

const styleMaskedText = css`
  -webkit-text-security: disc;
`

const styleSecureFieldBase = css`
  color: ${colors.secondary20};
  border-radius: 3px;
  border: ${BORDER_SIZE}px solid ${colors.secondary70};
  outline: none;
  &::placeholder {
    color: ${colors.secondary70};
  }
  &:focus, &:hover {
    border: ${BORDER_SIZE}px solid ${colors.primary40};
  }
  background-image: linear-gradient(
    135deg,
    ${STRIPES_COLOR} 0%,
    ${STRIPES_COLOR} ${STRIPES_WIDTH_PERCENT}%,
    transparent ${STRIPES_WIDTH_PERCENT}%,
    transparent 50%,
    ${STRIPES_COLOR} 50%,
    ${STRIPES_COLOR} ${50 + STRIPES_WIDTH_PERCENT}%,
    transparent ${50 + STRIPES_WIDTH_PERCENT}%,
    transparent
  );
  background-size: ${STRIPES_BOX_SIZE_PX}px ${STRIPES_BOX_SIZE_PX}px;
  ${props => props.isError && styleInputAsError}
`

const StyledClearButton = styled(IconLink)`
  position: absolute;
  right: 0;
  padding: 10px;
  padding-right: 12px
`

const IconHolder = styled.div`
  position: absolute;
  pointer-events: none;
  right: 0;
  padding: 8px;
  padding-right: 8px
`

const StyledMaskedDynamicTextArea = styled(DynamicTextArea)`
  textarea {
    ${styleSecureFieldBase}
    ${styleMaskedText}
  }
`

const StyledInputField = styled.input`
  display: flex;
  flex: 1;
  font-weight: 400;
  font-size: 14px;
  letter-spacing: -0.2px;
  line-height: 20px;
  height: ${HEIGHT - 2 * BORDER_SIZE}px;
  padding: 0 ${PADDING_HORIZ}px 0 ${PADDING_HORIZ}px;
  ${styleSecureFieldBase}
  ${props => (props.withClearIcon || props.withCustomIcon) && styleInputWithIcon}
`

const SecureInputField = ({
  value, showClear, className, width, maxLength, onKeyDown, onChange,
  placeholder, isError, disabled, iconComponent, dataTest, multiline, minRows,
}) => {
  const [prevValue, setPrevValue] = useState('')

  const onChange_ = (event) => {
    const value = event.target.value
    onChange(value)
  }

  const onClear = () => {
    onChange('')
  }

  const onFocus = (event) => {
    setPrevValue(event.target.value)
    onClear()
  }

  const onBlur = (event) => {
    onChange(event.target.value || prevValue)
  }

  const showClearIcon = value && showClear && !disabled
  const withCustomIcon = Boolean(iconComponent)
  const showOuterClearIcon = Boolean(showClearIcon && !multiline)
  const multilineRows = minRows || 5

  if (multiline) {
    return (
      <StyledMaskedDynamicTextArea
        className={className}
        small
        value={value}
        onChange={onChange_}
        placeholder={placeholder}
        minRows={multilineRows}
        maxRows={multilineRows + 5}
        isError={isError}
        disabled={disabled}
      />
    )
  }

  return (
    <StyledInputFieldWrapper className={className} width={width}>
      <StyledInputField
        maxLength={maxLength || 120}
        type='password'
        value={value}
        onChange={onChange_}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        isError={isError}
        disabled={disabled}
        data-test={dataTest}
        autocomplete='new-password'
        withClearIcon={showOuterClearIcon}
        withCustomIcon={withCustomIcon}
      />
      {showOuterClearIcon && (
        <StyledClearButton
          iconName='icon-clear-search'
          onClick={onClear}
        />
      )}
      {withCustomIcon && (
        <IconHolder>
          {iconComponent}
        </IconHolder>
      )}
    </StyledInputFieldWrapper>
  )
}

SecureInputField.propTypes = {
  value: PropTypes.string,
  showClear: PropTypes.bool,
  width: PropTypes.string,
  maxLength: PropTypes.number,
  onKeyDown: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  isError: PropTypes.bool,
  disabled: PropTypes.bool,
  iconComponent: PropTypes.node,
  dataTest: PropTypes.string,
  multiline: PropTypes.bool,
  minRows: PropTypes.number,
}

export default SecureInputField
